import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Heart, ShoppingBag, X, Plus, Minus, ArrowLeft, Gift, Eye, Trash2, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './ProductsPage.css';
import './GiftingCategoryPage.css';

const bannerBg = "/images/g banner.png";

const CATEGORY_CONFIGS = {
  'for-her': {
    title: 'Exquisite Gifts For Her',
    titleMr: 'तिच्यासाठी उत्कृष्ट भेटवस्तू',

    subtitle: 'Rings, Gold Bracelets, Chains & Traditional Mekhalas',
    subtitleMr: 'अंगठ्या, सोन्याचे ब्रेसलेट, चेन आणि पारंपारिक मेखला',

    collageImgs: [
      '/images/w ring1.jpg',
      '/images/w br14.jpg',
      '/images/w chain13.jpg',
      '/images/waist19.jpg'
    ],

    collageLabels: [
      'Elegant Rings',
      'Gold Bracelets',
      'Elegant Chains',
      'Traditional Mekhala'
    ],

    collageLabelsMr: [
      'सुंदर अंगठ्या',
      'सोन्याचे ब्रेसलेट',
      'सुंदर चेन',
      'पारंपारिक मेखला'
    ],

    targetWhom: 'For Her',

    tagline: 'Timeless Elegance Crafting Forever Memories',
    taglineMr: 'कायमची आठवण राहील अशी उत्कृष्ट कलाकुसर',

    subCats: [
      'All',
      'Rings',
      'Bracelets',
      'Chains',
      'Mekhala (Waist Belt)',
      'Necklace',
      'Earrings',
      'Daily Wear',
      'Jodvi (Toe Rings)',
      'Painjan (Anklets)'
    ]
  },

  'for-him': {
    title: 'Handsome Gifts For Him',
    titleMr: 'त्याच्यासाठी खास भेटवस्तू',

    subtitle: 'Cuban Link Bracelets, Platinum Chains & Black Onyx Rings',
    subtitleMr: 'क्युबन लिंक ब्रेसलेट, प्लॅटिनम चेन आणि ब्लॅक ओनिक्स अंगठ्या',

    collageImgs: [
      '/images/men br8.jpg',
      '/images/men ring2.jpg',
      '/images/men chain3.jpg',
      '/images/men br10.jpg'
    ],

    collageLabels: [
      'Cuban Link Bracelets',
      'Classic Rings',
      'Platinum Chains',
      'Statement Bracelets'
    ],

    collageLabelsMr: [
      'क्युबन लिंक ब्रेसलेट',
      'क्लासिक अंगठ्या',
      'प्लॅटिनम चेन',
      'स्टेटमेंट ब्रेसलेट'
    ],

    targetWhom: 'For Him',

    tagline: 'Bold Style. Timeless Craftsmanship.',
    taglineMr: 'दमदार शैली आणि कालातीत कलाकुसर',

    subCats: [
      'All',
      'Rings',
      'Bracelets',
      'Chains',
      'Kad-Dora'
    ]
  },

  'for-baby': {
    title: 'Precious Gifts For Baby & Kids',
    titleMr: 'लहान मुलांसाठी अनमोल भेटवस्तू',

    subtitle: 'Pure 925 Silver Painjans, Charms & Lightweight Bangles',
    subtitleMr: 'शुद्ध 925 चांदीचे पैंजण, चार्म्स आणि हलक्या बांगड्या',

    collageImgs: [
      '/images/painjan5.jpg',
      '/images/painjan7.jpg',
      '/images/painjan9.jpg',
      '/images/painjan10.jpg'
    ],

    collageLabels: [
      'Silver Painjans',
      'Delicate Painjans',
      'Baby Bracelets',
      'Little Charms'
    ],

    collageLabelsMr: [
      'चांदीचे पैंजण',
      'नाजूक पैंजण',
      'लहान मुलांचे ब्रेसलेट',
      'गोंडस तोड-वाळे'
    ],

    targetWhom: 'For Baby',

    tagline: 'Auspicious Blessings for Tender Little Ones',
    taglineMr: 'लहान मुलांसाठी शुभ आशीर्वाद',

    subCats: [
      'All',
      'Painjan (Anklets)',
      'Bracelets',
      'Kad-Dora',
      'Wale-Tode',
      'Mangati (Bracelets)'
    ]
  },

  'birthday': {
    title: 'Birthday Gift Collection',
    titleMr: 'वाढदिवस भेटवस्तू संग्रह',

    subtitle: 'Solitaire Rings, Diamond Chains & Modern Bangles',
    subtitleMr: 'सॉलिटेअर अंगठ्या, डायमंड चेन आणि आधुनिक बांगड्या',

    collageImgs: [
      '/images/w ring1.jpg',
      '/images/w chain.jpg',
      '/images/w br2.jpg',
      '/images/w ring3.jpg'
    ],

    collageLabels: [
      'Birthday Rings',
      'Diamond Chains',
      'Modern Bracelets',
      'Elegant Rings'
    ],

    collageLabelsMr: [
      'वाढदिवसाच्या अंगठ्या',
      'डायमंड चेन',
      'आधुनिक ब्रेसलेट',
      'सुंदर अंगठ्या'
    ],

    targetOccasion: 'Birthday',

    tagline: 'Make Their Special Day Unforgettable',
    taglineMr: 'त्यांचा खास दिवस अविस्मरणीय बनवा',

    subCats: [
      'All',
      'Rings',
      'Chains',
      'Bracelets',
      'Necklace',
      'Kada',
      'Painjan (Anklets)',
      'Wale-Tode'
    ]
  },

  'anniversary': {
    title: 'Anniversary Gift Collection',
    titleMr: 'लग्नाचा वाढदिवस संग्रह',

    subtitle: 'Royal Kundan Sets, Silver Bands & Heritage Necklaces',
    subtitleMr: 'रॉयल कुंदन सेट, चांदीचे बँड आणि हेरिटेज नेकलेस',

    collageImgs: [
      '/images/w chain13.jpg',
      '/images/men ring2.jpg',
      '/images/w ring3.jpg',
      '/images/w chain14.jpg'
    ],

    collageLabels: [
      'Heritage Necklace',
      'Classic Gold Bands',
      'Couple Rings',
      'Royal Necklace'
    ],

    collageLabelsMr: [
      'हेरिटेज नेकलेस',
      'क्लासिक सोन्याचे बँड',
      'कपल रिंग्ज',
      'रॉयल नेकलेस'
    ],

    targetOccasion: 'Anniversary',

    tagline: 'Celebrate Years of Togetherness in Pure Gold',
    taglineMr: 'शुद्ध सोन्यात वर्षानुवर्षांचा सहवास साजरा करा',

    audienceFilters: ['All', 'For Women', 'For Men'],
    subCats: [
      'All',
      'Rings',
      'Bracelets',
      'Chains',
      'Kada',
      'Earrings',
      'Necklace',
      'Painjan (Anklets)'
    ]
  },

  'wedding': {
    title: 'Wedding & Bridal Gifting',
    titleMr: 'लग्न आणि वधूसाठी भेटवस्तू',

    subtitle: 'Complete Bridal Mekhalas, Heavy Payals & Necklace Combos',
    subtitleMr: 'संपूर्ण ब्राइडल मेखला, जड पैंजण आणि नेकलेस कॉम्बो',

    collageImgs: [
      '/images/waist12.jpg',
      '/images/painjan1.jpg',
      '/images/w chain14.jpg',
      '/images/w chain13.jpg'
    ],

    collageLabels: [
      'Bridal Mekhala',
      'Heavy Payals',
      'Bridal Necklace',
      'Necklace Combo'
    ],

    collageLabelsMr: [
      'ब्राइडल मेखला',
      'जड पैंजण',
      'वधूसाठी नेकलेस',
      'नेकलेस कॉम्बो'
    ],

    targetOccasion: 'Wedding',

    tagline: 'Grand Heirlooms Crafted for the Royal Bride',
    taglineMr: 'शाही वधूसाठी बनवलेले भव्य दागिने',

    audienceFilters: ['All', 'For Women', 'For Men'],
    subCats: [
      'All',
      'Rings',
      'Bracelets',
      'Chains',
      'Kada',
      'Earrings',
      'Necklace',
      'Painjan (Anklets)'
    ]
  },

  'engagement': {
    title: 'Engagement Ring Collection',
    titleMr: 'साखरपुडा अंगठी संग्रह',

    subtitle: 'Forever Solitaires, Dual-Tone Bands & Diamond Accents',
    subtitleMr: 'कायमस्वरूपी सॉलिटेअर, ड्युअल-टोन बँड आणि डायमंड अ‍ॅक्सेंट',

    collageImgs: [
      '/images/w ring3.jpg',
      '/images/men ring.jpg',
      '/images/w ring1.jpg',
      '/images/men ring2.jpg'
    ],

    collageLabels: [
      'Forever Solitaire',
      'Couple Band',
      'Diamond Ring',
      'Classic Men’s Ring'
    ],

    collageLabelsMr: [
      'फॉरएव्हर सॉलिटेअर',
      'कपल बँड',
      'डायमंड रिंग',
      'क्लासिक पुरुषांची अंगठी'
    ],

    targetOccasion: 'Engagement',

    tagline: 'The Perfect Symbol for Your Forever Promise',
    taglineMr: 'तुमच्या कायमच्या वचनासाठी योग्य प्रतीक',

    audienceFilters: ['All', 'For Women', 'For Men'],
    subCats: [
      'All',
      'Rings',
      'Bracelets',
      'Chains',
      'Kada',
      'Earrings',
      'Necklace',
      'Painjan (Anklets)'
    ]
  },

  'valentines-day': {
    title: "Valentine's Day Special Collection",
    titleMr: 'व्हॅलेंटाईन्स डे विशेष संग्रह',

    subtitle: 'Heart-Cut Diamond Rings, Rose Gold Chains & Couple Bands',
    subtitleMr: 'हार्ट-कट डायमंड रिंग्ज, रोज गोल्ड चेन आणि कपल बँड',

    collageImgs: [
      '/images/w ring14.jpg',
      '/images/w br2.jpg',
      '/images/w chain2.jpg',
      '/images/w ring1.jpg'
    ],

    collageLabels: [
      'Heart-Cut Rings',
      'Romantic Bracelets',
      'Rose Gold Chains',
      'Couple Rings'
    ],

    collageLabelsMr: [
      'हार्ट-कट रिंग्ज',
      'रोमँटिक ब्रेसलेट',
      'रोज गोल्ड चेन',
      'कपल रिंग्ज'
    ],

    targetOccasion: "Valentine's Day",

    tagline: 'Celebrate Eternal Love & Passion',
    taglineMr: 'शाश्वत प्रेम आणि उत्कटता साजरी करा',

    audienceFilters: ['All', 'For Women', 'For Men'],

    subCats: [
      'All',
      'Rings',
      'Chains',
      'Bracelets'
    ]
  },

  'silver-idols': {
    title: 'Silver Idols & Divine Artefacts',
    titleMr: 'चांदीच्या मूर्ती आणि कलाकृती',

    subtitle: 'Pure 925 Hallmarked Laxmi, Ganesh & Pooja Artefacts',
    subtitleMr: 'शुद्ध 925 हॉलमार्क लक्ष्मी, गणेश आणि पूजा कलाकृती',

    /*
      IMPORTANT:
      Replace these 4 filenames with the actual silver-idol
      images available inside public/images.
    */
    collageImgs: [
      '/images/silver laxmi.jpg',
      '/images/silver ganesh.jpg',
      '/images/silver pooja.jpg',
      '/images/silver idol.jpg'
    ],

    collageLabels: [
      'Laxmi Idol',
      'Ganesh Idol',
      'Pooja Artefacts',
      'Divine Silver Idol'
    ],

    collageLabelsMr: [
      'लक्ष्मी मूर्ती',
      'गणेश मूर्ती',
      'पूजा कलाकृती',
      'दैवी चांदीची मूर्ती'
    ],

    targetCategory: 'Silver Idols',

    tagline: 'Gifts That Carry Divine Blessings & Prosperity',
    taglineMr: 'दैवी आशीर्वाद आणि समृद्धी घेऊन येणाऱ्या भेटवस्तू',

    subCats: [
      'All',
      'Silver Idols',
      'Payal (Anklets)'
    ]
  }
};

const AUDIENCE_FILTERS = ['All', 'For Women', 'For Men', 'For Baby'];
const PRICE_RANGES = [
  { label: 'Below ₹2,000', labelMr: '₹२,००० च्या खाली', min: 0, max: 2000 },
  { label: '₹2,000 – ₹5,000', labelMr: '₹२,००० – ₹५,०००', min: 2000, max: 5000 },
  { label: '₹5,000 – ₹10,000', labelMr: '₹५,००० – ₹१०,०००', min: 5000, max: 10000 },
  { label: '₹10,000 – ₹20,000', labelMr: '₹१०,००० – ₹२०,०००', min: 10000, max: 20000 },
  { label: 'Over ₹20,000', labelMr: '₹२०,००० च्या वर', min: 20000, max: Infinity }
];
const RING_SIZES = ['1', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30'];
const WEIGHT_RANGES = ['0-2g', '2-5g', '5-10g', '10-20g'];


const imageVariants = {
  tl: {
    hidden: {
      x: -80,
      y: -50,
      opacity: 0,
      scale: 0.94,
      rotate: 0
    },
    show: {
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  },

  tr: {
    hidden: {
      x: 80,
      y: -50,
      opacity: 0,
      scale: 0.94,
      rotate: 0
    },
    show: {
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.9,
        delay: 0.08,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  },

  bl: {
    hidden: {
      x: -70,
      y: 60,
      opacity: 0,
      scale: 0.94,
      rotate: 0
    },
    show: {
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.9,
        delay: 0.16,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  },

  br: {
    hidden: {
      x: 70,
      y: 60,
      opacity: 0,
      scale: 0.94,
      rotate: 0
    },
    show: {
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.9,
        delay: 0.24,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }
};

const GiftingCategoryPage = () => {
  const { catSlug } = useParams();
  const navigate = useNavigate();
  const { gifts, products, wishlist, addToCart, toggleWishlist, t, lang, cart, updateCartQuantity } = useContext(AppContext);
  const isMr = lang === 'mr';
  const priceFont = isMr ? "'Poppins', sans-serif" : "'Playfair Display', serif";

  const getLocalizedContent = (item, field) => {
    if (isMr && item[`${field}Mr`]) {
      return item[`${field}Mr`];
    }
    return item[field];
  };

  const config = CATEGORY_CONFIGS[catSlug] || CATEGORY_CONFIGS['for-her'];

  const [selectedItem, setSelectedItem] = useState(null);
  const [modalQty, setModalQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState('Standard');
  const [isBuyNowFlow, setIsBuyNowFlow] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [selectedSubCat, setSelectedSubCat] = useState('All');
  const [selectedAudience, setSelectedAudience] = useState('All');
  const [selectedRingSizes, setSelectedRingSizes] = useState([]);
  const [selectedWeightRanges, setSelectedWeightRanges] = useState([]);
  const [plusSizeFilter, setPlusSizeFilter] = useState('all');

  const togglePriceRange = (range) => {
    setSelectedPriceRanges(prev =>
      prev.some(r => r.label === range.label)
        ? prev.filter(r => r.label !== range.label)
        : [...prev, range]
    );
  };

  const toggleRingSize = (sz) => {
    setSelectedRingSizes(prev =>
      prev.includes(sz) ? prev.filter(s => s !== sz) : [...prev, sz]
    );
  };

  const toggleWeightRange = (w) => {
    setSelectedWeightRanges(prev =>
      prev.includes(w) ? prev.filter(s => s !== w) : [...prev, w]
    );
  };

  const clearAllFilters = () => {
    setSelectedPriceRanges([]);
    setSelectedSubCat('All');
    setSelectedAudience('All');
    setSelectedRingSizes([]);
    setSelectedWeightRanges([]);
    setPlusSizeFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = (
    selectedPriceRanges.length > 0 ||
    selectedSubCat !== 'All' ||
    selectedAudience !== 'All' ||
    searchQuery.trim() !== '' ||
    selectedRingSizes.length > 0 ||
    selectedWeightRanges.length > 0 ||
    plusSizeFilter !== 'all'
  );

  const categoryItems = [...gifts, ...products].filter(item => {
    if (config.targetWhom) return item.forWhom === config.targetWhom || item.subCategory === config.targetWhom;
    if (config.targetOccasion) return item.occasion === config.targetOccasion;
    if (config.targetCategory) return item.category === config.targetCategory || item.name.toLowerCase().includes('silver');
    return true;
  });

  const isOccasionPage = ['birthday', 'anniversary', 'wedding', 'engagement', 'valentines-day', 'baby-naming-ceremony'].includes(catSlug);

  const filteredItems = categoryItems.filter(item => {
    // 1. Search Query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchSearch =
        (item.name && item.name.toLowerCase().includes(q)) ||
        (item.nameMr && item.nameMr.toLowerCase().includes(q)) ||
        (item.category && item.category.toLowerCase().includes(q)) ||
        (item.subCategory && item.subCategory.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q)) ||
        (item.descriptionMr && item.descriptionMr.toLowerCase().includes(q));
      if (!matchSearch) return false;
    }

    // 2. Price Range (Multiple checkboxes matching ProductsPage)
    if (selectedPriceRanges.length > 0) {
      const matchPrice = selectedPriceRanges.some(r => item.price >= r.min && item.price < r.max);
      if (!matchPrice) return false;
    }

    // 3. SubCategory
    if (selectedSubCat !== 'All') {
      const cleanSubCat = selectedSubCat.split('(')[0].trim().toLowerCase();
      const itemCat = (item.category || '').toLowerCase();
      const itemSub = (item.subCategory || '').toLowerCase();
      const itemName = (item.name || '').toLowerCase();
      if (!itemCat.includes(cleanSubCat) && !itemSub.includes(cleanSubCat) && !itemName.includes(cleanSubCat)) {
        return false;
      }
    }

    // 4. Audience / For Whom
    if (selectedAudience !== 'All') {
      const matchW = selectedAudience === 'For Women' && (item.subCategory === 'For Women' || item.forWhom === 'For Her');
      const matchM = selectedAudience === 'For Men' && (item.subCategory === 'For Men' || item.forWhom === 'For Him');
      const matchB = selectedAudience === 'For Baby' && (item.subCategory === 'For Baby' || item.forWhom === 'For Baby');
      if (!matchW && !matchM && !matchB) return false;
    }

    // 5. Ring Size
    if (selectedRingSizes.length > 0) {
      const itemSizes = Array.isArray(item.sizes) ? item.sizes : (item.ringSize ? [String(item.ringSize)] : []);
      const matchSize = selectedRingSizes.some(sz => itemSizes.includes(sz) || (item.ringSize && String(item.ringSize) === sz));
      if (!matchSize) return false;
    }

    // 6. Weight Range
    if (selectedWeightRanges.length > 0) {
      if (!item.weight || !selectedWeightRanges.includes(item.weight)) {
        return false;
      }
    }

    // 7. Plus Size
    if (isOccasionPage && plusSizeFilter === 'plus-size' && !item.plusSize) {
      return false;
    }

    return true;
  });

  const getRelated = (item) => {
    const pool = [...gifts, ...products];
    return pool.filter(p => p.category === item.category && p.id !== item.id).slice(0, 4);
  };

  const openModal = (item, buyNow = false) => {
    setSelectedItem(item);
    setModalQty(1);
    setSelectedSize(item.ringSize || 'Standard');
    setIsBuyNowFlow(buyNow);
  };

  return (
    <div className="gifting-cat-page">
      <section
        className="collage-header-section"
        style={{
          backgroundImage: `url("${bannerBg}")`
        }}
      >
        <div className="collage-bg-overlay"></div>

        <div className="collage-header-inner">

          {/* BACK BUTTON */}
          <div className="collage-breadcrumbs">

            <button
              className="btn-cat-back"
              onClick={() => navigate('/gifting')}
            >
              <ArrowLeft size={16} />

              {isMr
                ? 'सर्व भेटवस्तूंच्या श्रेणी'
                : 'All Gift Categories'}
            </button>

          </div>

          <div className="cat-gifting-header-wrap">

            {/* =========================
          LEFT TEXT
      ========================== */}
            <div className="cat-gifting-text-side">
              <h1>
                {isMr && config.titleMr
                  ? config.titleMr
                  : config.title}
              </h1>
            </div>

            {/* =========================
          RIGHT 4 IMAGE CARDS
      ========================== */}
            <div className="square-collage-container">

              {config.collageImgs.map((image, index) => {

                const positions = ['tl', 'tr', 'bl', 'br'];

                const label =
                  isMr && config.collageLabelsMr
                    ? config.collageLabelsMr[index]
                    : config.collageLabels[index];

                return (
                  <motion.div
                    key={`${image}-${index}`}
                    className={`sq-collage-item ${positions[index]}`}
                    variants={imageVariants[positions[index]]}
                    initial="hidden"
                    animate="show"
                  >

                    <img
                      src={image}
                      alt={label}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />

                    <div className="sq-collage-overlay"></div>

                    <div className="sq-collage-label">
                      <span>
                        {label}
                      </span>
                    </div>

                  </motion.div>
                );
              })}

            </div>

          </div>

        </div>
      </section>

      <div className="pp-layout gcp-layout-wrap">
        <aside className="pp-sidebar-expanded">
          <div className="pp-sidebar-header">
            <h3>{t.filters || (isMr ? 'फिल्टर्स' : 'Filters')}</h3>
            {hasActiveFilters && (
              <button className="pp-clear-all" onClick={clearAllFilters}>
                {t.clearAll || (isMr ? 'सर्व हटवा' : 'Clear All')}
              </button>
            )}
          </div>

          <div className="filter-card-block">
            <h4>{isMr ? 'श्रेणी' : 'Category'}</h4>
            <div className="filter-chips-grid">
              {config.subCats.map(sc => (
                <button
                  key={sc}
                  className={`filter-chip ${selectedSubCat === sc ? 'active' : ''}`}
                  onClick={() => setSelectedSubCat(sc)}
                >
                  {sc === 'All' && isMr ? 'सर्व' : sc}
                </button>
              ))}
            </div>
          </div>

          {(config.targetOccasion || config.audienceFilters) && (
            <div className="filter-card-block">
              <h4>{isMr ? 'कोणासाठी' : 'Audience'}</h4>
              <div className="filter-chips-grid">
                {(config.audienceFilters || AUDIENCE_FILTERS).map(aud => (
                  <button
                    key={aud}
                    className={`filter-chip ${selectedAudience === aud ? 'active' : ''}`}
                    onClick={() => setSelectedAudience(aud)}
                  >
                    {aud === 'All' && isMr ? 'सर्व' : aud}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isOccasionPage && (
            <div className="filter-card-block">
              <h4>{isMr ? 'प्लस साईझ' : 'Plus Size'}</h4>
              <div className="filter-chips-grid">
                {[
                  { id: 'all', label: isMr ? 'सर्व' : 'All' },
                  { id: 'plus-size', label: isMr ? 'फक्त प्लस साईझ' : 'Plus Size Only' }
                ].map(p => (
                  <button
                    key={p.id}
                    className={`filter-chip ${plusSizeFilter === p.id ? 'active' : ''}`}
                    onClick={() => setPlusSizeFilter(p.id)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="filter-card-block">
            <h4>{t.ringSize || (isMr ? 'रिंग साईझ' : 'Ring Size')}</h4>
            <div className="ring-sizes-grid">
              {RING_SIZES.map(sz => (
                <button
                  key={sz}
                  className={`ring-size-btn ${selectedRingSizes.includes(sz) ? 'active' : ''}`}
                  onClick={() => toggleRingSize(sz)}
                >
                  {sz}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-card-block">
            <h4>{t.weightRange || (isMr ? 'वजन श्रेणी' : 'Weight Range')}</h4>
            <div className="filter-chips-grid">
              {WEIGHT_RANGES.map(w => (
                <button
                  key={w}
                  className={`filter-chip ${selectedWeightRanges.includes(w) ? 'active' : ''}`}
                  onClick={() => toggleWeightRange(w)}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-card-block">
            <h4>{t.priceRange || (isMr ? 'किंमत श्रेणी' : 'Price Range')}</h4>
            {PRICE_RANGES.map(range => (
              <label key={range.label} className="pp-checkbox-row">
                <input
                  type="checkbox"
                  checked={selectedPriceRanges.some(r => r.label === range.label)}
                  onChange={() => togglePriceRange(range)}
                />
                <span className="pp-checkbox-custom"></span>
                <span className="pp-checkbox-label">{range.label}</span>
              </label>
            ))}
          </div>
        </aside>

        <main className="pp-main">
          <div className="pp-listing-header">
            <div className="pp-listing-header-top">
              <span className="pp-count-badge">
                <strong>{filteredItems.length}</strong> {isMr ? 'दागिने सापडले' : `piece${filteredItems.length !== 1 ? 's' : ''} found`}
              </span>

              <div className="pp-search-box">
                <Search size={18} className="pp-search-icon" />
                <input
                  type="text"
                  placeholder={isMr ? 'दागिने शोधा (उदा. रिंग, चेन, ब्रेसलेट)...' : 'Search gifts by name, category...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="pp-search-clear" onClick={() => setSearchQuery('')} title="Clear search">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {hasActiveFilters && (
              <div className="pp-active-filters">
                <span className="pp-active-label">{isMr ? 'सक्रिय फिल्टर:' : 'Active Filters:'}</span>
                {searchQuery && (
                  <span className="pp-filter-tag search-tag">
                    "{searchQuery}"
                    <button onClick={() => setSearchQuery('')}>×</button>
                  </span>
                )}
                {selectedSubCat !== 'All' && (
                  <span className="pp-filter-tag">
                    {selectedSubCat}
                    <button onClick={() => setSelectedSubCat('All')}>×</button>
                  </span>
                )}
                {selectedAudience !== 'All' && (
                  <span className="pp-filter-tag">
                    {selectedAudience}
                    <button onClick={() => setSelectedAudience('All')}>×</button>
                  </span>
                )}
                {plusSizeFilter !== 'all' && (
                  <span className="pp-filter-tag">
                    {isMr ? 'प्लस साईझ' : 'Plus Size'}
                    <button onClick={() => setPlusSizeFilter('all')}>×</button>
                  </span>
                )}
                {selectedRingSizes.map(sz => (
                  <span key={sz} className="pp-filter-tag">
                    Size {sz}
                    <button onClick={() => toggleRingSize(sz)}>×</button>
                  </span>
                ))}
                {selectedWeightRanges.map(w => (
                  <span key={w} className="pp-filter-tag">
                    {w}
                    <button onClick={() => toggleWeightRange(w)}>×</button>
                  </span>
                ))}
                {selectedPriceRanges.map(r => (
                  <span key={r.label} className="pp-filter-tag">
                    {r.label}
                    <button onClick={() => togglePriceRange(r)}>×</button>
                  </span>
                ))}
                <button className="pp-clear-filters-btn" onClick={clearAllFilters}>
                  {isMr ? 'सर्व साफ करा' : 'Clear All'}
                </button>
              </div>
            )}
          </div>

          {filteredItems.length === 0 ? (
            <div className="cat-empty-box">
              <Gift size={48} style={{ opacity: 0.3 }} />
              <p>{isMr ? 'निवडलेल्या फिल्टरशी जुळणारे कोणतेही दागिने नाहीत.' : 'No items found matching the selected filters.'}</p>
              <button onClick={clearAllFilters}>
                {isMr ? 'फिल्टर रीसेट करा' : 'Reset Filters'}
              </button>
            </div>
          ) : (
            <div className="pp-grid">
            {filteredItems.map(item => {
              const cartItem = (cart || []).find(ci => ci.product.id === item.id);
              return (
                <motion.div
                  key={item.id}
                  className="pcard"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  layout
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  <div className="pcard-img-wrap">
                    <img
                      src={item.image || 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="800" height="800"><rect width="100%" height="100%" fill="#f3efe7"/><circle cx="50%" cy="40%" r="18%" fill="#d6c4a1"/><rect x="18%" y="52%" width="64%" height="18%" rx="6" fill="#d6c4a1"/><text x="50%" y="72%" text-anchor="middle" font-size="30" fill="#6b5a46" font-family="Arial">No Image</text></svg>')}
                      alt={item.name}
                      className="pcard-img"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="800" height="800"><rect width="100%" height="100%" fill="#f3efe7"/><circle cx="50%" cy="40%" r="18%" fill="#d6c4a1"/><rect x="18%" y="52%" width="64%" height="18%" rx="6" fill="#d6c4a1"/><text x="50%" y="72%" text-anchor="middle" font-size="30" fill="#6b5a46" font-family="Arial">No Image</text></svg>');
                      }}
                    />

                    <div className="pcard-hover-overlay">
                      <button className="pcard-quickview-btn" onClick={() => openModal(item, false)}>
                        <Eye size={18} /> {t.quickView || (isMr ? 'झटपट पहा' : 'Quick View')}
                      </button>
                    </div>

                    <button
                      className={`pcard-fav-btn ${wishlist.includes(item.id) ? 'active' : ''}`}
                      onClick={() => toggleWishlist(item.id)}
                    >
                      <Heart size={16} fill={wishlist.includes(item.id) ? '#d4af37' : 'none'} color={wishlist.includes(item.id) ? '#d4af37' : '#fff'} />
                    </button>

                    <span className="pcard-material-badge">{item.material || item.subCategory}</span>
                  </div>

                  <div className="pcard-body">
                    <span className="pcard-cat">{item.category}</span>
                    <h3 className="pcard-name" onClick={() => openModal(item, false)}>
                      {getLocalizedContent(item, 'name')}
                    </h3>



                    <div className="pcard-price-row">
                      <span className="pcard-price" style={{ fontFamily: priceFont }}>
                        <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
                        <span className="pcard-amount" style={{ fontFamily: priceFont }}>{item.price.toLocaleString()}</span>
                      </span>
                    </div>

                    <div className="pcard-actions">
                      {cartItem ? (
                        <div className="amazon-qty-btn-group">
                          <button
                            className="amazon-qty-btn-minus"
                            onClick={() => updateCartQuantity(cartItem.itemKey || cartItem.product.id, cartItem.quantity - 1)}
                          >
                            {cartItem.quantity > 1 ? <Minus size={14} /> : <Trash2 size={14} />}
                          </button>
                          <span className="amazon-qty-value">{cartItem.quantity}</span>
                          <button
                            className="amazon-qty-btn-plus"
                            onClick={() => updateCartQuantity(cartItem.itemKey || cartItem.product.id, cartItem.quantity + 1)}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      ) : (
                        <button className="pcard-btn-cart" onClick={() => addToCart(item, 1)}>
                          <ShoppingBag size={15} /> {t.addToCart}
                        </button>
                      )}
                      <button className="pcard-btn-buy" onClick={() => openModal(item, true)}>
                        {t.buyNow}
                      </button>
                    </div>
                  </div>

                  <div className="pcard-shimmer"></div>
                </motion.div>
              );
            })}
          </div>
        )}
        </main>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <motion.div
            className="pp-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setSelectedItem(null)}
          >
            <motion.div
              className="pp-modal pp-modal-noscroll"
              initial={{ opacity: 0, scale: 0.92, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 30 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            >
              <button className="pp-modal-close" onClick={() => setSelectedItem(null)}>
                <X size={22} />
              </button>

              <div className="pp-modal-main">
                <div className="pp-modal-img-col">
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.name}
                  />
                </div>

                <div className="pp-modal-info-col">
                  <span className="pp-modal-cat">{selectedItem.category}</span>
                  <h2>
                    {getLocalizedContent(selectedItem, 'name')}
                  </h2>

                  <div className="pp-modal-price" style={{ fontFamily: priceFont }}>
                    <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
                    <span className="pcard-amount" style={{ fontFamily: priceFont }}>{selectedItem.price.toLocaleString()}</span>
                  </div>

                  {(selectedItem.material || (selectedItem.weight && selectedItem.weight !== '0' && selectedItem.weight !== 0) || selectedItem.subCategory) && (
                    <div className="pp-modal-details">
                      {selectedItem.material ? (
                        <div className="pp-modal-detail-item">
                          <span className="detail-label">{isMr ? 'धातू' : 'Material'}:</span>
                          <span className="detail-value">{selectedItem.material}</span>
                        </div>
                      ) : null}
                      {selectedItem.weight && selectedItem.weight !== '0' && selectedItem.weight !== 0 ? (
                        <div className="pp-modal-detail-item">
                          <span className="detail-label">{isMr ? 'वजन' : 'Weight'}:</span>
                          <span className="detail-value">{typeof selectedItem.weight === 'number' || !String(selectedItem.weight).toLowerCase().includes('g') ? `${selectedItem.weight} g` : selectedItem.weight}</span>
                        </div>
                      ) : null}
                      {selectedItem.subCategory ? (
                        <div className="pp-modal-detail-item">
                          <span className="detail-label">{isMr ? 'श्रेणी' : 'Category'}:</span>
                          <span className="detail-value">{selectedItem.subCategory}</span>
                        </div>
                      ) : null}
                    </div>
                  )}

                  <p className="pp-modal-desc">
                    {getLocalizedContent(selectedItem, 'description')}
                  </p>

                  <div className="pp-sizes-group">
                    <span className="sizes-label">{t.availableSizes}</span>
                    <div className="sizes-chips">
                      {(selectedItem.category?.toLowerCase().includes('ring')
                        ? ['6', '7', '8', '9', '10', '12', '14', '16', '18', '20']
                        : selectedItem.category?.toLowerCase().includes('chain')
                          ? ['16"', '18"', '20"', '22"']
                          : ['S', 'M', 'L', 'Free Size']
                      ).map(sz => (
                        <button
                          key={sz}
                          className={`size-chip ${selectedSize === sz ? 'active' : ''}`}
                          onClick={() => setSelectedSize(sz)}
                        >
                          {sz}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pp-modal-qty-row">
                    <span>{t.quantity}</span>
                    <div className="pp-qty-control">
                      <button onClick={() => setModalQty(q => Math.max(1, q - 1))}><Minus size={14} /></button>
                      <span>{modalQty}</span>
                      <button onClick={() => setModalQty(q => q + 1)}><Plus size={14} /></button>
                    </div>
                    <span className="pp-modal-subtotal" style={{ fontFamily: priceFont }}>
                      <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
                      <span className="pcard-amount" style={{ fontFamily: priceFont }}>{(selectedItem.price * modalQty).toLocaleString()}</span>
                    </span>
                  </div>

                  <div className="pp-modal-btns">
                    <button
                      className="btn-pp-add-cart"
                      onClick={() => {
                        addToCart(selectedItem, modalQty, selectedSize);
                        if (isBuyNowFlow) {
                          setSelectedItem(null);
                          navigate(`/contact?subject=Buy Now - ${encodeURIComponent(selectedItem.name)} (Size: ${selectedSize || 'Standard'})`);
                        } else {
                          setSelectedItem(null);
                        }
                      }}
                    >
                      <ShoppingBag size={16} /> {isBuyNowFlow ? t.buyNow : `${t.addToCart} (${modalQty})`}
                    </button>
                    {!isBuyNowFlow && (
                      <button
                        className="btn-pp-buy-now"
                        onClick={() => {
                          addToCart(selectedItem, modalQty, selectedSize);
                          setSelectedItem(null);
                          navigate(`/contact?subject=Buy Now - ${encodeURIComponent(selectedItem.name)} (Size: ${selectedSize || 'Standard'})`);
                        }}
                      >
                        {t.buyNow}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {getRelated(selectedItem).length > 0 && (
                <div className="pp-modal-related">
                  <h3>{t.samePattern}</h3>
                  <div className="pp-related-grid">
                    {getRelated(selectedItem).map(rp => (
                      <motion.div
                        key={rp.id}
                        className="pp-related-card"
                        whileHover={{ scale: 1.03 }}
                        onClick={() => openModal(rp, false)}
                      >
                        <img src={rp.image} alt={rp.name} />
                        <h5>{getLocalizedContent(rp, 'name')}</h5>
                        <p className="pp-related-price" style={{ fontFamily: priceFont }}>
                          <span className="pcard-currency" style={{ fontFamily: priceFont }}>₹</span>
                          <span className="pcard-amount" style={{ fontFamily: priceFont }}>{rp.price.toLocaleString()}</span>
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GiftingCategoryPage;
